// Pure helpers for payment-card (`type === 'card'`) VaultItems: brand detection,
// Luhn validation, number grouping, expiry formatting, and last-4 masking. Kept
// out of any client so web + extension share one implementation; Android and iOS
// port these (parity-tested against card.test.ts). Display/convenience only —
// NOT part of the byte contract. See docs/superpowers/specs/
// 2026-10-11-credit-card-storage-and-autofill-design.md.

import type { CardBrand } from './index';

/** Strip everything but digits (spaces, dashes, unicode separators). */
function digitsOnly(s: string | undefined | null): string {
  return (s ?? '').replace(/\D+/g, '');
}

/**
 * Best-effort card network from the IIN/BIN prefix. Approximate (brand ranges
 * overlap and evolve) and for display only; resolves as more digits are typed
 * and returns `'unknown'` for an empty/unrecognized number. Order matters —
 * more specific prefixes are tested first.
 */
export function detectCardBrand(cardNumber: string): CardBrand {
  const n = digitsOnly(cardNumber);
  if (!n) return 'unknown';

  // American Express: 34, 37
  if (/^3[47]/.test(n)) return 'amex';
  // Diners Club: 300–305, 3095, 36, 38, 39
  if (/^3(?:0[0-5]|095|6|8|9)/.test(n)) return 'diners';
  // JCB: 3528–3589
  if (/^35(?:2[89]|[3-8]\d)/.test(n)) return 'jcb';
  // Visa: 4
  if (/^4/.test(n)) return 'visa';
  // Mastercard: 51–55, or 2221–2720
  if (/^5[1-5]/.test(n)) return 'mastercard';
  if (/^2/.test(n) && n.length >= 4) {
    const p4 = parseInt(n.slice(0, 4), 10);
    if (p4 >= 2221 && p4 <= 2720) return 'mastercard';
  }
  // Discover: 6011, 644–649, 65
  if (/^6(?:011|4[4-9]|5)/.test(n)) return 'discover';
  // UnionPay: 62, 81
  if (/^(?:62|81)/.test(n)) return 'unionpay';
  // Maestro: 50, 56–58, 63, 67
  if (/^(?:5[0678]|6[37])/.test(n)) return 'maestro';
  return 'unknown';
}

/**
 * Luhn checksum. Returns true only for a plausible-length (12–19 digit) number
 * that passes the mod-10 check. Use for a soft "this doesn't look right"
 * warning — never a hard save-block (a user may store a card we mis-score).
 */
export function luhnValid(cardNumber: string): boolean {
  const n = digitsOnly(cardNumber);
  if (n.length < 12 || n.length > 19) return false;
  let sum = 0;
  let double = false;
  for (let i = n.length - 1; i >= 0; i--) {
    let d = n.charCodeAt(i) - 48; // '0' === 48
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/** Last four digits of the number (empty string if none). */
export function cardLast4(cardNumber: string): string {
  return digitsOnly(cardNumber).slice(-4);
}

/** Masked display form: `•••• 1234` (empty string for an empty number). */
export function maskedCardNumber(cardNumber: string): string {
  const last4 = cardLast4(cardNumber);
  return last4 ? `•••• ${last4}` : '';
}

function groupDigits(n: string, template: number[]): string {
  const parts: string[] = [];
  let i = 0;
  for (const size of template) {
    if (i >= n.length) break;
    parts.push(n.slice(i, i + size));
    i += size;
  }
  while (i < n.length) {
    parts.push(n.slice(i, i + 4));
    i += 4;
  }
  return parts.join(' ');
}

/**
 * Space-grouped number for display and for formatted fills: Amex `4-6-5`,
 * Diners `4-6-4`, otherwise `4-4-4-4` (remainder chunked in 4s). Brand is
 * derived when not supplied.
 */
export function formatCardNumber(cardNumber: string, brand?: CardBrand): string {
  const n = digitsOnly(cardNumber);
  if (!n) return '';
  const b = brand ?? detectCardBrand(n);
  const template = b === 'amex' ? [4, 6, 5] : b === 'diners' ? [4, 6, 4] : [4, 4, 4, 4];
  return groupDigits(n, template);
}

export type ExpiryStyle = 'MM/YY' | 'MM/YYYY';

/**
 * Expiry string from the stored two-digit month + four-digit year, in the
 * requested style (`MM/YY` default, or `MM/YYYY`). Returns `''` if either part
 * is missing. Tolerates loosely-formatted inputs (pads/truncates).
 */
export function formatExpiry(
  month: string | undefined,
  year: string | undefined,
  style: ExpiryStyle = 'MM/YY',
): string {
  const m = digitsOnly(month);
  const y = digitsOnly(year);
  if (!m || !y) return '';
  const mm = m.padStart(2, '0').slice(-2);
  const yyyy = y.padStart(4, '0').slice(-4);
  const yy = yyyy.slice(-2);
  return style === 'MM/YYYY' ? `${mm}/${yyyy}` : `${mm}/${yy}`;
}
